'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Cart } from '@/lib/shopify/types';
import {
  addToCartClient,
  createCartClient,
  getCartClient,
  removeFromCartClient,
  updateCartClient,
} from '@/lib/shopify/client';

interface CartLine {
  variantId: string;
  quantity: number;
  attributes?: { key: string; value: string }[];
}

interface CartContextType {
  cart: Cart | null;
  isLoading: boolean;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (variantId: string, quantity?: number, attributes?: { key: string; value: string }[]) => Promise<void>;
  addItems: (lines: CartLine[]) => Promise<void>;
  updateItem: (lineId: string, quantity: number) => Promise<void>;
  removeItem: (lineId: string) => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_ID_KEY = 'shopify_cart_id';

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCartState] = useState<Cart | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const setCart = useCallback((next: Cart | null) => {
    setCartState(next);
    if (typeof window !== 'undefined') {
      if (next?.id) {
        localStorage.setItem(CART_ID_KEY, next.id);
      } else {
        localStorage.removeItem(CART_ID_KEY);
      }
    }
  }, []);

  useEffect(() => {
    const initializeCart = async () => {
      const cartId = localStorage.getItem(CART_ID_KEY);

      if (cartId) {
        try {
          const existingCart = await getCartClient(cartId);

          if (existingCart) {
            setCart(existingCart);
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error fetching cart:', error);
        }
      }

      try {
        const newCart = await createCartClient();
        setCart(newCart);
      } catch (error) {
        console.error('Error creating cart:', error);
      }

      setIsLoading(false);
    };

    initializeCart();
  }, [setCart]);

  const ensureCart = useCallback(async () => {
    if (cart) return cart;
    const newCart = await createCartClient();
    setCart(newCart);
    return newCart;
  }, [cart, setCart]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const addItem = useCallback(
    async (variantId: string, quantity: number = 1, attributes?: { key: string; value: string }[]) => {
      setIsLoading(true);

      try {
        const activeCart = await ensureCart();
        const nextCart = await addToCartClient(activeCart.id, [
          { merchandiseId: variantId, quantity, attributes },
        ]);
        setCart(nextCart);
      } catch (error) {
        console.error('Error adding to cart:', error);
      }

      setIsLoading(false);
    },
    [ensureCart, setCart]
  );

  const addItems = useCallback(
    async (lines: CartLine[]) => {
      if (lines.length === 0) return;

      setIsLoading(true);

      try {
        const activeCart = await ensureCart();
        const nextCart = await addToCartClient(
          activeCart.id,
          lines.map((line) => ({
            merchandiseId: line.variantId,
            quantity: line.quantity,
            attributes: line.attributes,
          }))
        );
        setCart(nextCart);
      } catch (error) {
        console.error('Error adding items to cart:', error);
      }

      setIsLoading(false);
    },
    [ensureCart, setCart]
  );

  const updateItem = useCallback(
    async (lineId: string, quantity: number) => {
      if (!cart) return;

      setIsLoading(true);

      try {
        const nextCart = await updateCartClient(cart.id, [{ id: lineId, quantity }]);
        setCart(nextCart);
      } catch (error) {
        console.error('Error updating cart:', error);
      }

      setIsLoading(false);
    },
    [cart, setCart]
  );

  const removeItem = useCallback(
    async (lineId: string) => {
      if (!cart) return;

      setIsLoading(true);

      try {
        const nextCart = await removeFromCartClient(cart.id, [lineId]);
        setCart(nextCart);
      } catch (error) {
        console.error('Error removing from cart:', error);
      }

      setIsLoading(false);
    },
    [cart, setCart]
  );

  return (
    <CartContext.Provider
      value={{
        cart,
        isLoading,
        isOpen,
        openCart,
        closeCart,
        addItem,
        addItems,
        updateItem,
        removeItem,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);

  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }

  return context;
}
